import type { Theme } from "@mui/material/styles";

type Styles = Record<string, any>;

/**
 * Control and action recipes.
 *
 * Elevation: panels use container / raised / popover. Clickable chrome on a
 * panel must step up to surface.control and never reuse the parent fill.
 *
 * Action roles:
 * - primary: confirm, submit, okay, save, buy, go
 * - secondary: dismiss cancel, close, back (control fill, not a second gradient)
 * - danger: remove, delete, bust, cancel listing/offer
 * - caution: expensive but not destructive (heal, refill, crime attempt)
 *
 * create / info / neutral gradients are not generic button colors.
 * Keep create only on the landing Create Account CTA.
 */

const buttonType = (theme: Theme): Styles => ({
  color: theme.design.color.text.primary,
  borderRadius: theme.design.radius.md,
  fontFamily: theme.design.typography.fontFamily,
  fontWeight: 900,
  textTransform: "none" as const,
});

/** Dropdowns, selects, inputs, and quiet buttons sitting on a panel. */
export const controlSurface = (theme: Theme): Styles => ({
  background: theme.design.color.surface.control,
  backgroundColor: theme.design.color.surface.control,
  color: theme.design.color.text.primary,
  border: `1px solid ${theme.design.color.border.black}`,
  "&:hover": {
    background: theme.design.color.surface.controlHover,
    backgroundColor: theme.design.color.surface.controlHover,
  },
  "&:disabled, &.Mui-disabled": {
    background: theme.design.color.action.disabled,
    backgroundColor: theme.design.color.action.disabled,
    color: theme.design.color.text.muted,
  },
});

const gradientAction = (theme: Theme, gradient: string, color?: string): Styles => ({
  ...buttonType(theme),
  background: gradient,
  color: color ?? theme.design.color.text.primary,
  border: "none",
  transition: `opacity ${theme.design.motion.duration.normal}`,
  "&:hover": {
    background: gradient,
    opacity: theme.design.opacity.hover,
  },
  "&:disabled, &.Mui-disabled": {
    background: theme.design.color.action.disabled,
    color: theme.design.color.text.muted,
    opacity: 1,
  },
});

export const actionPrimary = (theme: Theme): Styles =>
  gradientAction(theme, theme.design.gradient.action.primary);

export const actionSecondary = (theme: Theme): Styles => ({
  ...controlSurface(theme),
  ...buttonType(theme),
  boxShadow: theme.design.shadow.sm,
  "&:hover": {
    background: theme.design.color.surface.controlHover,
    backgroundColor: theme.design.color.surface.controlHover,
  },
  "&:disabled, &.Mui-disabled": {
    background: theme.design.color.action.disabled,
    backgroundColor: theme.design.color.action.disabled,
    color: theme.design.color.text.muted,
    boxShadow: "none",
  },
});

export const actionDanger = (theme: Theme): Styles =>
  gradientAction(theme, theme.design.gradient.action.danger);

export const actionCaution = (theme: Theme): Styles =>
  gradientAction(
    theme,
    theme.design.gradient.action.warning,
    theme.design.color.text.inverse
  );
